import {
  AIStarsIcon,
  Button,
  Callout,
  Disclaimer,
  Div,
  H5,
  Icon,
  LoadingDots,
  ModalPortal,
  P,
  Separator,
  ServiceFeeSuggestion,
  ServiceFeeSuggestionMonth,
  displayCentsInDollars,
  handleError,
  lightTheme,
} from '@vgrubs/components';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import {
  BulletList,
  MonthRow,
  SuggestedFee,
} from './PerOrderFeeSuggestionModal.styled';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  /** True from the click until the answer or an error lands. */
  isGenerating: boolean;
  data: ServiceFeeSuggestion | undefined;
  error: unknown;
  onRetry: () => void;
  /** Puts the cents in the field. Nothing is saved until the modal's Save. */
  onUse: (fee: number) => void;
};

/**
 * A 4xx comes with a sentence written for the admin (no location, a target
 * out of range) and would fail the same way again; only a 5xx or a dropped
 * connection is worth a Try Again.
 */
const describeError = (error: unknown) => {
  if (isAxiosError(error) && error.response && error.response.status < 500)
    return {
      message: handleError(error) || 'No suggestion is available.',
      canRetry: false,
    };
  return { message: 'The AI suggestion failed.', canRetry: true };
};

/** Which monthly amount the fee was worked out for, in the backend's words. */
const describeTarget = (data: ServiceFeeSuggestion) => {
  const target = displayCentsInDollars(data.monthlyTarget);
  switch (data.targetSource) {
    case 'request':
      return `Based on your target of ${target}/month.`;
    case 'monthlyFee':
      return `Based on the saved monthly fee of ${target}.`;
    case 'default':
      return `No monthly fee set - using the standard ${target}.`;
    // A backend without the field yet.
    default:
      return `Based on a target of ${target}/month.`;
  }
};

const monthLabel = (month: string) =>
  DateTime.fromFormat(month, 'yyyy-MM').toFormat('LLL yyyy');

const MonthLine = ({
  month,
  projectedOrders,
}: {
  month: ServiceFeeSuggestionMonth;
  /** Only for the running month: where its order count is heading. */
  projectedOrders: number | null;
}) => (
  <MonthRow>
    <div>
      <P $m="0">{monthLabel(month.month)}</P>
      {month.partial && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          In progress
        </Disclaimer>
      )}
    </div>
    <div>
      <P $m="0">{month.orders}</P>
      {month.partial && projectedOrders != null && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          ~{Math.round(projectedOrders)} projected
        </Disclaimer>
      )}
    </div>
    <P $m="0">{displayCentsInDollars(month.averagePayout)}</P>
    <div>
      <P $m="0">{displayCentsInDollars(month.collectedAtRecommended)}</P>
      {month.ordersBelowRecommended > 0 && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {month.ordersBelowRecommended} below the fee
        </Disclaimer>
      )}
    </div>
  </MonthRow>
);

const SuggestionBody = ({
  data,
  onUse,
}: {
  data: ServiceFeeSuggestion;
  onUse: (fee: number) => void;
}) => {
  const fee = data.recommendedFee;

  // No virtual-brand orders: nothing to base an amount on, the summary says why.
  if (fee == null) return <P $m="0">{data.summary}</P>;

  const stats = [
    `~${Math.round(data.baselineMonthlyOrders)} orders/month`,
    `avg ${displayCentsInDollars(data.expectedMonthlyCollection)}/month`,
    `worst month ${displayCentsInDollars(data.worstMonthCollection)}`,
  ];

  return (
    <>
      <Div $flex $alignCenter $between $gap="16px" $columnSM $alignStartSM>
        <div>
          <SuggestedFee>{displayCentsInDollars(fee)}</SuggestedFee>
          <P $m="0" $color={lightTheme.lightGray}>
            per order
          </P>
        </div>
        <Button $selected $w="auto" $wSM="100%" onClick={() => onUse(fee)}>
          Use this amount
        </Button>
      </Div>

      <Disclaimer $m="12px 0 0" $color={lightTheme.lightGray}>
        {describeTarget(data)}
      </Disclaimer>

      <P $m="16px 0 0">{data.summary}</P>
      <Disclaimer $m="8px 0 0" $color={lightTheme.lightGray}>
        {stats.join(' · ')}
      </Disclaimer>

      {!data.targetReachable && (
        <Div $mt="16">
          <Callout tone="danger">
            Even $5.00 per order would not reach the monthly target.
          </Callout>
        </Div>
      )}

      {data.debtPlanPerOrderAmount != null && (
        <Div $mt="16">
          <Callout tone="info">
            The debt repayment plan also takes{' '}
            {displayCentsInDollars(data.debtPlanPerOrderAmount)} per order,
            after this fee.
          </Callout>
        </Div>
      )}

      {data.reasoning.length > 0 && (
        <>
          <Separator $m="16px 0" />
          <H5 $mb="8">Why this amount</H5>
          <BulletList>
            {data.reasoning.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </BulletList>
        </>
      )}

      {data.risks.length > 0 && (
        <>
          <H5 $m="16px 0 8px">Watch out for</H5>
          <Callout tone="warning">
            <BulletList>
              {data.risks.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </BulletList>
          </Callout>
        </>
      )}

      {data.months.length > 0 && (
        <>
          <Separator $m="16px 0" />
          <H5 $mb="4">Order history</H5>
          {/* The list can reach further back than the average does. */}
          {data.baselineMonths.length > 0 && (
            <Disclaimer $m="0 0 4px" $color={lightTheme.lightGray}>
              The amount is based on{' '}
              {data.baselineMonths.map(monthLabel).join(', ')}.
            </Disclaimer>
          )}
          <MonthRow>
            {[
              'Month',
              'Orders',
              'Avg payout',
              `At ${displayCentsInDollars(fee)}`,
            ].map((label) => (
              <Disclaimer key={label} $m="0" $color={lightTheme.lightGray}>
                {label}
              </Disclaimer>
            ))}
          </MonthRow>
          {data.months.map((month) => (
            <MonthLine
              key={month.month}
              month={month}
              projectedOrders={data.currentMonthProjectedOrders}
            />
          ))}
        </>
      )}
    </>
  );
};

/**
 * The AI's per-order vTablet fee, opened from the services modal (hence the
 * portal and the z-index above it).
 *
 * `glow` is passed on every render on purpose: the modal mounts its glow
 * wrapper whenever the prop is present, so the border fades out when the
 * answer lands instead of the whole modal remounting (see modal.tsx).
 */
export const PerOrderFeeSuggestionModal = ({
  isOpen,
  closeModal,
  isGenerating,
  data,
  error,
  onRetry,
  onUse,
}: Props) => {
  const errorInfo = !isGenerating && error ? describeError(error) : null;

  return (
    <ModalPortal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      textAlign="left"
      width="600px"
      zIndex={110}
      glow={isGenerating}
    >
      <Div $flex $alignCenter $gap="8px" $mb="16">
        <Icon>
          <AIStarsIcon />
        </Icon>
        <H5 $m="0">Per-order fee suggestion</H5>
      </Div>

      {isGenerating ? (
        <Div $m="8px 0">
          {/* The dots are a span, and P paints spans red unless told otherwise. */}
          <P
            $m="0"
            $center
            $color={lightTheme.lightGray}
            $spanColor={lightTheme.lightGray}
          >
            Working out the amount
            <LoadingDots active />
          </P>
          <Disclaimer $m="4px 0 0" $center $color={lightTheme.lightGray}>
            This usually takes a few seconds.
          </Disclaimer>
        </Div>
      ) : errorInfo ? (
        <>
          <P $m="0">{errorInfo.message}</P>
          {errorInfo.canRetry && (
            <Button
              $blueGradient
              $selected
              $w="100%"
              $mt="16"
              onClick={onRetry}
            >
              Try Again
            </Button>
          )}
        </>
      ) : data ? (
        // No note for `vTabletEnabled: false`: it is the SAVED switch, and the
        // button only shows while the form's switch is on, so it would only
        // ever tell an admin turning vTablet on that it is off.
        <SuggestionBody data={data} onUse={onUse} />
      ) : null}

      <Separator $m="16px 0 12px" />
      <Disclaimer $m="0" $color={lightTheme.lightGray}>
        The amount is calculated from this location's virtual-brand orders. The
        explanation around it is written by AI.
      </Disclaimer>
    </ModalPortal>
  );
};
